package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.CreditCard
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.ijoin.getPlatform
import com.ijoin.i18n.LocalLocaleManager
import com.ijoin.i18n.strings
import com.ijoin.payment.StripeCheckoutOverlay
import com.ijoin.ui.design.EventCardAccent
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAccentSoft
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardCompactCorner
import com.ijoin.ui.design.EventCardOutline
import com.ijoin.ui.design.EventCardRailContent
import com.ijoin.ui.design.EventCardSurface
import com.ijoin.ui.design.EventOutlineButton
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventRoundIconButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.payment.PaymentFlowArgs
import com.ijoin.ui.payment.PaymentViewModel

@Composable
fun PaymentScreen(
    args: PaymentFlowArgs,
    onBackClick: () -> Unit,
    onTicketReady: (String) -> Unit,
    modifier: Modifier = Modifier,
    viewModel: PaymentViewModel = remember(args) { PaymentViewModel(args) },
) {
    val state by viewModel.uiState.collectAsState()
    val strings = strings()
    val localeManager = LocalLocaleManager.current
    val stripeLocale = localeManager.currentLanguage.code
    val platform = getPlatform()
    val supportsInlineCheckout = platform.isWeb || platform.isMobile

    DisposableEffect(Unit) {
        onDispose { viewModel.dispose() }
    }

    DisposableEffect(supportsInlineCheckout, state.clientSecret, state.publishableKey, state.paymentCompleted) {
        val canInstallStripe = supportsInlineCheckout &&
            state.clientSecret != null &&
            state.publishableKey != null &&
            !state.paymentCompleted
        if (canInstallStripe) {
            StripeCheckoutOverlay.present(
                publishableKey = state.publishableKey!!,
                clientSecret = state.clientSecret!!,
                locale = stripeLocale,
                onSuccess = { viewModel.handleStripeSuccess() },
                onError = { viewModel.handleStripeError(it) },
            )
        }
        onDispose {
            if (supportsInlineCheckout) {
                StripeCheckoutOverlay.dispose()
            }
        }
    }

    LaunchedEffect(state.issuedTicketId) {
        val ticketId = state.issuedTicketId
        if (ticketId != null) {
            viewModel.onTicketHandled()
            onTicketReady(ticketId)
        }
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(eventBackgroundBrush())
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 20.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(20.dp),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                EventRoundIconButton(
                    icon = Icons.AutoMirrored.Filled.ArrowBack,
                    contentDescription = strings.back,
                    onClick = onBackClick
                )
                Text(
                    text = strings.paymentTitle,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    color = EventCardAccentDark,
                    modifier = Modifier.weight(1f)
                )
            }

            val contentModifier = Modifier
                .fillMaxWidth()
                .widthIn(max = 560.dp)
                .align(Alignment.CenterHorizontally)

            EventSurfaceCard(
                modifier = contentModifier,
                backgroundColor = EventCardSurface,
                borderColor = EventCardOutline
            ) {
                Icon(
                    imageVector = Icons.Filled.CreditCard,
                    contentDescription = null,
                    tint = EventCardAccent,
                )
                Text(
                    text = strings.paymentSubtitle,
                    style = MaterialTheme.typography.headlineSmall,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Start,
                    color = EventCardAccentDark
                )
                Text(
                    text = strings.paymentDescription,
                    style = MaterialTheme.typography.bodyMedium,
                    color = EventCardRailContent
                )

                when {
                    state.isLoading -> {
                        CircularProgressIndicator(color = EventCardAccent)
                        Text(
                            text = strings.paymentPreparing,
                            style = MaterialTheme.typography.bodyMedium,
                            color = EventCardAccentDark,
                        )
                    }

                    state.isFetchingTickets -> {
                        CircularProgressIndicator(color = EventCardAccent)
                        Text(
                            text = strings.paymentFetchingTickets,
                            style = MaterialTheme.typography.bodyMedium,
                            color = EventCardAccentDark,
                        )
                    }

                    state.paymentCompleted -> {
                        Text(
                            text = strings.paymentCompletedMessage,
                            style = MaterialTheme.typography.bodyLarge,
                            color = EventCardAccentDark,
                        )
                    }

                    state.clientSecret != null && state.publishableKey != null -> {
                        EventSurfaceCard(
                            modifier = Modifier.fillMaxWidth(),
                            cornerRadius = EventCardCompactCorner,
                            backgroundColor = EventCardAccentSoft,
                            borderColor = EventCardOutline,
                            shadowElevation = 0.dp,
                            contentPadding = androidx.compose.foundation.layout.PaddingValues(12.dp),
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Text(
                                text = strings.paymentStripePopupHint,
                                style = MaterialTheme.typography.bodyMedium,
                                color = EventCardAccentDark,
                            )
                            if (state.error != null) {
                                Text(
                                    text = state.error ?: "",
                                    color = EventCardAlert,
                                    style = MaterialTheme.typography.bodySmall,
                                )
                            }
                        }
                    }

                    state.error != null -> {
                        Text(
                            text = state.error ?: "",
                            color = EventCardAlert,
                            style = MaterialTheme.typography.bodyMedium,
                        )
                    }
                }
            }

            if (!state.isLoading) {
                EventPrimaryButton(
                    text = strings.paymentRetryButton,
                    onClick = { viewModel.retry() },
                    enabled = !state.paymentCompleted,
                    modifier = contentModifier
                )
            }

            EventOutlineButton(
                text = strings.back,
                onClick = onBackClick,
                modifier = contentModifier
            )

            state.ticketFetchError?.let { error ->
                EventSurfaceCard(
                    modifier = contentModifier,
                    cornerRadius = EventCardCompactCorner,
                    backgroundColor = EventCardAccentSoft,
                    borderColor = EventCardOutline,
                    shadowElevation = 0.dp,
                    contentPadding = androidx.compose.foundation.layout.PaddingValues(12.dp),
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text(
                        text = error,
                        color = EventCardAlert,
                        style = MaterialTheme.typography.bodyMedium,
                    )
                    EventPrimaryButton(
                        text = strings.paymentRetryTicketsButton,
                        onClick = { viewModel.retryFetchTickets() },
                        modifier = Modifier.fillMaxWidth()
                    )
                }
            }
        }
    }
}
